import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { 
  LayoutDashboard, Users, ChevronDown, ChevronRight, 
  Briefcase, BarChart3, Settings, Shield, Calendar,
  Megaphone, Newspaper, Gift, Mic, MessageSquare,
  Target, UserPlus, DollarSign, Eye, Lightbulb,
  ClipboardCheck, Rocket, GraduationCap, LogOut, Clock,
  BookOpen, HelpCircle, UserPlus2, FileText, Building2, MailCheck, ClipboardList, ChefHat, TrendingUp
} from 'lucide-react';

const SidebarSection = ({ title, icon: Icon, children, defaultOpen = false, badge, onTitleClick }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const handleTitleClick = (e) => {
    if (onTitleClick) {
      e.stopPropagation();
      onTitleClick();
    }
  };

  const handleChevronClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex items-center justify-between w-full px-3 py-2 text-neutral-300 hover:bg-white/5 rounded-lg transition-colors">
        <button 
          onClick={onTitleClick ? handleTitleClick : () => setIsOpen(!isOpen)}
          className="flex items-center gap-2 flex-1 text-left"
        >
          <Icon className="w-4 h-4" />
          <span className="text-sm font-medium">{title}</span>
          {badge && (
            <Badge className="ml-1 bg-[#39FF14] text-neutral-900 text-xs px-1.5 py-0">
              {badge}
            </Badge>
          )}
        </button>
        <button onClick={handleChevronClick} className="p-1 hover:bg-white/10 rounded">
          {isOpen ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </button>
      </div>
      <CollapsibleContent className="pl-4 space-y-0.5 mt-1">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

const SidebarItem = ({ icon: Icon, label, onClick, badge, active, className = '' }) => (
  <button
    onClick={onClick}
    className={`flex items-center justify-between w-full px-3 py-2 text-sm rounded-lg transition-colors ${
      active 
        ? 'bg-white/10 text-white' 
        : 'text-neutral-400 hover:bg-white/5 hover:text-neutral-200'
    } ${className}`}
  >
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </div>
    {badge && (
      <Badge variant="secondary" className="bg-white/10 text-xs">
        {badge}
      </Badge>
    )}
  </button>
);

const CondensedSidebar = ({
  user,
  isAdmin,
  activeSection,
  stats = {},
  onNavigate,
  onLogout
}) => {
  return (
    <div className="flex flex-col h-full bg-neutral-900 text-white">
      {/* Logo/Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#39FF14] rounded-lg flex items-center justify-center">
            <span className="text-neutral-900 font-bold text-sm">MB</span>
          </div>
          <div>
            <h1 className="font-semibold text-sm">My Biz Consulting</h1>
            <p className="text-xs text-neutral-400">Consultant Panel</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-3 border-b border-white/10">
        <p className="text-xs text-neutral-500 uppercase tracking-wider mb-2 px-1">Quick Actions</p>
        <div className="grid grid-cols-3 gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="flex flex-col items-center gap-1 h-auto py-2 text-neutral-300 hover:text-white hover:bg-white/10"
              >
                <UserPlus className="w-4 h-4" />
                <span className="text-xs">Add</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuItem onClick={() => onNavigate('addClient')}>
                <UserPlus className="w-4 h-4 mr-2" />
                Add New Client
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onNavigate('inviteCommunity')}>
                <Users className="w-4 h-4 mr-2" />
                Invite to Community
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="sm"
            className="flex flex-col items-center gap-1 h-auto py-2 text-neutral-300 hover:text-white hover:bg-white/10"
            onClick={() => onNavigate('meeting')}
          >
            <Calendar className="w-4 h-4" />
            <span className="text-xs">Meet</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="flex flex-col items-center gap-1 h-auto py-2 text-neutral-300 hover:text-white hover:bg-white/10"
            onClick={() => onNavigate('bulletin')}
          >
            <Megaphone className="w-4 h-4" />
            <span className="text-xs">Post</span>
          </Button>
        </div>
      </div>

      {/* Navigation Sections */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {/* Overview - Always visible */}
        <SidebarItem
          icon={LayoutDashboard}
          label="Overview"
          active={activeSection === 'overview'}
          onClick={() => onNavigate('overview')}
        />
        
        {/* Projects - Direct link */}
        <SidebarItem
          icon={Briefcase}
          label="Projects"
          onClick={() => onNavigate('projects')}
        />

        {/* Clients Section */}
        <SidebarSection title="Clients" icon={Users} defaultOpen={true} badge={stats.totalClients}>
          <SidebarItem
            icon={Target}
            label="Pipeline"
            badge={stats.pendingClients}
            onClick={() => onNavigate('pipeline')}
          />
          <SidebarItem
            icon={ClipboardCheck}
            label="Leads"
            badge={stats.leads}
            onClick={() => onNavigate('leads')}
          />
          <SidebarItem
            icon={Rocket}
            label="Scale Applications"
            badge={stats.scaleApps}
            onClick={() => onNavigate('scaleApps')}
          />
          <SidebarItem
            icon={Briefcase}
            label="Cases / CRM"
            onClick={() => onNavigate('cases')}
          />
          <SidebarItem
            icon={Building2}
            label="Companies"
            onClick={() => onNavigate('companies')}
          />
          <SidebarItem
            icon={FileText}
            label="Service Quotes"
            onClick={() => onNavigate('serviceQuotes')}
          />
          <SidebarItem
            icon={ClipboardList}
            label="Client Onboarding"
            onClick={() => onNavigate('clientOnboarding')}
          />
        </SidebarSection>

        {/* Programs Section */}
        <SidebarSection title="Programs" icon={GraduationCap}>
          <SidebarItem
            icon={Target}
            label="R.A.P.I.D.™"
            onClick={() => onNavigate('rapid')}
          />
          <SidebarItem
            icon={Mic}
            label="Podcast Academy"
            onClick={() => onNavigate('podcast')}
          />
          <SidebarItem
            icon={GraduationCap}
            label="Academy Training"
            onClick={() => onNavigate('academyTraining')}
          />
          <SidebarItem
            icon={Rocket}
            label="Strategic Scaling"
            onClick={() => onNavigate('scaling')}
          />
        </SidebarSection>

        {/* Content Section */}
        <SidebarSection title="Content" icon={Megaphone}>
          <SidebarItem
            icon={Megaphone}
            label="Bulletin Board"
            onClick={() => onNavigate('bulletin')}
          />
          <SidebarItem
            icon={Newspaper}
            label="Newsletters"
            onClick={() => onNavigate('newsletters')}
          />
          <SidebarItem
            icon={Calendar}
            label="Events"
            badge={stats.events}
            onClick={() => onNavigate('events')}
          />
          <SidebarItem
            icon={BookOpen}
            label="Blog"
            onClick={() => onNavigate('blog')}
          />
        </SidebarSection>

        {/* Community Section */}
        <SidebarSection 
          title="Community" 
          icon={MessageSquare} 
          badge={stats.community}
          onTitleClick={() => onNavigate('communityManagement')}
        >
          <SidebarItem
            icon={Users}
            label="Community Hub"
            onClick={() => onNavigate('communityHub')}
          />
          <SidebarItem
            icon={Eye}
            label="Community View"
            onClick={() => onNavigate('community')}
          />
          <SidebarItem
            icon={Lightbulb}
            label="Recommendations"
            onClick={() => onNavigate('recommendations')}
            badge={stats.recommendations > 0 ? stats.recommendations : null}
          />
          <SidebarItem
            icon={Gift}
            label="Referrals"
            badge={stats.referrals}
            onClick={() => onNavigate('referrals')}
          />
          <SidebarItem
            icon={ChefHat}
            label="Miles Agent"
            onClick={() => onNavigate('milesAgent')}
          />
          <SidebarItem
            icon={MailCheck}
            label="Mass Outreach"
            onClick={() => onNavigate('outreach')}
          />
        </SidebarSection>

        {/* Tools Section */}
        <SidebarSection title="Tools" icon={BarChart3}>
          <SidebarItem
            icon={BarChart3}
            label="Dashboard"
            onClick={() => onNavigate('analytics')}
          />
          <SidebarItem
            icon={TrendingUp}
            label="Client Progress"
            onClick={() => onNavigate('clientProgress')}
          />
          <SidebarItem
            icon={MailCheck}
            label="Progress Reports"
            onClick={() => onNavigate('progressReports')}
          />
          <SidebarItem
            icon={DollarSign}
            label="Payments"
            onClick={() => onNavigate('payments')}
          />
          <SidebarItem
            icon={FileText}
            label="Billing & Invoices"
            onClick={() => onNavigate('billing')}
          />
          <SidebarItem
            icon={TrendingUp}
            label="Sales Agents"
            onClick={() => onNavigate('salesAgents')}
          />
          <SidebarItem
            icon={Building2}
            label="Partnership Agent"
            onClick={() => onNavigate('partnershipAgent')}
          />
          <SidebarItem
            icon={TrendingUp}
            label="Activity Scores"
            onClick={() => onNavigate('activityScores')}
          />
          <SidebarItem
            icon={FileText}
            label="Proposal Generator"
            onClick={() => onNavigate('proposals')}
          />
          <SidebarItem
            icon={Users}
            label="Agent Management"
            onClick={() => onNavigate('agentManagement')}
          />
          <SidebarItem
            icon={Building2}
            label="Formation Orders"
            onClick={() => onNavigate('formationOrders')}
          />
          <SidebarItem
            icon={FileText}
            label="Sponsorship Gateway"
            onClick={() => onNavigate('sponsorship')}
          />
          <SidebarItem
            icon={Building2}
            label="Sponsor Portals"
            onClick={() => onNavigate('sponsorPortals')}
          />
          <SidebarItem
            icon={Clock}
            label="Time Budget"
            onClick={() => onNavigate('timeBudget')}
          />
          <SidebarItem
            icon={MailCheck}
            label="Invite Analytics"
            onClick={() => onNavigate('inviteAnalytics')}
          />
          <SidebarItem
            icon={Shield}
            label="Security"
            onClick={() => onNavigate('security')}
          />
        </SidebarSection>

        {/* Support Links */}
        <div className="pt-2 mt-2 border-t border-white/10">
          <SidebarItem
            icon={DollarSign}
            label="Pricing & Plans"
            onClick={() => onNavigate('pricing')}
          />
          <SidebarItem
            icon={BookOpen}
            label="Onboarding Guide"
            onClick={() => onNavigate('onboarding')}
          />
          <SidebarItem
            icon={HelpCircle}
            label="Help & Docs"
            onClick={() => onNavigate('help')}
          />
          <SidebarItem
            icon={Settings}
            label="Settings"
            onClick={() => onNavigate('settings')}
          />
        </div>
      </div>

      {/* User Section */}
      <div className="p-3 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#39FF14]/20 flex items-center justify-center">
            <span className="text-[#39FF14] font-medium text-sm">
              {user?.name?.charAt(0) || 'C'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name || 'Consultant'}</p>
            <p className="text-xs text-neutral-400 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-neutral-400 hover:text-red-400 hover:bg-red-500/10"
          onClick={onLogout}
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );
};

export default CondensedSidebar;
